/// <reference path="./turndown-plugin-gfm.d.ts" />
import type { JSONContent } from "@tiptap/core";
import { generateHTML } from "@tiptap/html";
import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";
import { getContentExtensions } from "./extensions.js";
import { FRONT_MATTER_NODE_NAME, serializeFrontMatter } from "./frontMatter.js";

const contentExtensions = getContentExtensions();

function getAttribute(node: Node, name: string): string | null {
  return "getAttribute" in node && typeof node.getAttribute === "function"
    ? node.getAttribute(name)
    : null;
}

function flattenTableCellContent(content: string): string {
  return content.replace(/\n+/g, " ").trim().replace(/\|/g, "\\|");
}

function createTurndown(): TurndownService {
  const service = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
    emDelimiter: "*",
  });
  service.use(gfm);

  service.addRule("taskItemWithCheckedState", {
    filter: (node) => node.nodeName === "LI" && getAttribute(node, "data-checked") !== null,
    replacement: (content, node) => {
      const checked = getAttribute(node, "data-checked") === "true" ? "x" : " ";
      const itemContent = content
        .replace(/^\s+/, "")
        .replace(/\n\s*\n/g, "\n")
        .trim();
      return `- [${checked}] ${itemContent}\n`;
    },
  });

  // The GFM plugin's own `tableCell` rule doesn't collapse the block-level
  // whitespace (`\n\n`) that the paragraph rule wraps cell content in, so a
  // <td><p>text</p></td> cell renders as "| \n\ntext\n\n |" — a broken table
  // row. Override it (added after `gfm`, so it takes precedence per turndown's
  // most-recently-added-rule-wins resolution) to flatten cell content onto a
  // single line, keeping the same `| cell |` shape as the original rule.
  service.addRule("tableCellFlatten", {
    filter: ["th", "td"],
    replacement: (content, node) => {
      const parent = node.parentNode;
      const index = parent ? Array.prototype.indexOf.call(parent.childNodes, node) : -1;
      const prefix = index === 0 ? "| " : " ";
      return `${prefix}${flattenTableCellContent(content)} |`;
    },
  });

  return service;
}

// turndown-plugin-gfm@1.0.2's `isHeadingRow` detects the header row by checking
// that a table's first <tbody> has no preceding sibling (or only an empty
// <thead>). @tiptap/extension-table renders a leading <colgroup> (for column
// widths) as the table's first child, so the <tbody> is no longer "first" from
// the plugin's point of view — it silently falls back to `keep()` and emits
// the raw HTML table instead of GFM markdown. Markdown tables have no
// column-width concept anyway, so colgroups carry no convertible information;
// stripping the tag from the HTML string before parsing (turndown's `keep`
// check inspects the parsed DOM directly, so a turndown *rule* can't intercept
// it — the tag must be gone before parsing) is safe and restores real table
// conversion. See documentToMarkdown.test.ts "renders GFM tables".
const COLGROUP_RE = /<colgroup>[\s\S]*?<\/colgroup>/g;
const TABLE_RE = /<table\b[\s\S]*?<\/table>/gi;
const FIRST_ROW_RE = /<tr\b[^>]*>[\s\S]*?<\/tr>/i;
const TABLE_CELL_RE = /<(?:td|th)\b[^>]*>/gi;

function addEmptyHeaderToHeaderlessTable(tableHtml: string): string {
  const firstRow = tableHtml.match(FIRST_ROW_RE)?.[0];
  if (!firstRow || /<th\b/i.test(firstRow)) {
    return tableHtml;
  }

  const columnCount = firstRow.match(TABLE_CELL_RE)?.length ?? 0;
  if (columnCount === 0) {
    return tableHtml;
  }

  const headerCells = Array.from({ length: columnCount }, () => "<th></th>").join("");
  const header = `<thead><tr>${headerCells}</tr></thead>`;
  return tableHtml.replace(/(<tbody\b[^>]*>)/i, `${header}$1`);
}

function normalizeTables(html: string): string {
  return html.replace(COLGROUP_RE, "").replace(TABLE_RE, addEmptyHeaderToHeaderlessTable);
}

// 규칙이 고정되어 있고 turndown() 호출은 무상태이므로, 문서마다 새로
// 인스턴스를 만들 필요 없이(plugin 재등록·rule 재추가 비용 회피) 한 번만 생성해 재사용한다.
const turndownService = createTurndown();

const BACKTICK_RUN_RE = /`+/g;

/** 마지막 필드 뒤 개행이 없어도 종료 경계가 단독 `---` 줄이 되게 한다. */
function frontMatterBlock(yaml: string): string {
  if (yaml === "") return "---\n---";
  return `---\n${yaml.endsWith("\n") ? yaml : `${yaml}\n`}---`;
}

/**
 * 선두가 아닌 `frontMatter` 노드는 삭제하지 않고 fenced `yaml`로 보존한다.
 * YAML 원문에 backtick이 있어도 코드 블록이 조기 종료되지 않도록 가장 긴
 * backtick 연속보다 하나 긴 fence(최소 3)를 사용한다.
 */
function fencedYaml(yaml: string): string {
  const longest = Math.max(
    0,
    ...Array.from(yaml.matchAll(BACKTICK_RUN_RE), (match) => match[0].length)
  );
  const fence = "`".repeat(Math.max(3, longest + 1));
  const fencedContent = yaml.endsWith("\n") ? yaml : `${yaml}\n`;
  return `${fence}yaml\n${fencedContent}${fence}`;
}

/**
 * Tiptap/ProseMirror JSON → Markdown.
 * 1) 공유 스키마 확장으로 정적 HTML을 생성하고(@tiptap/html, 브라우저/Node 모두 안전)
 * 2) turndown + GFM 플러그인으로 마크다운화한다(표/체크리스트/취소선 지원).
 *
 * `frontMatter` 노드만 Turndown 일반 규칙에서 분리한다. 문서 첫 노드인 카드는 표준
 * `---` 경계로, 뒤쪽/중복 카드는 원래 위치에 fenced `yaml`로 출력한다.
 */
export function documentJsonToMarkdown(json: JSONContent): string {
  const segments: string[] = [];
  let ordinary: JSONContent[] = [];

  const flushOrdinary = () => {
    if (ordinary.length === 0) return;
    const html = normalizeTables(generateHTML({ ...json, content: ordinary }, contentExtensions));
    segments.push(turndownService.turndown(html).trim());
    ordinary = [];
  };

  (json.content ?? []).forEach((block, index) => {
    if (block.type !== FRONT_MATTER_NODE_NAME) {
      ordinary.push(block);
      return;
    }
    flushOrdinary();
    const { yaml } = serializeFrontMatter(block);
    segments.push(index === 0 ? frontMatterBlock(yaml) : fencedYaml(yaml));
  });
  flushOrdinary();

  return `${segments.filter(Boolean).join("\n\n")}\n`;
}
